import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from "recharts";

import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';

import { parseCustomTimestamp } from '../../utils/dateUtils';

const SentimentChart = () => {
    const { data } = useCallData();
    const { dateRange } = useWeek();

    // Process data to get sentiment counts per day for the selected date range
    const processCallData = () => {
        if (!dateRange || !dateRange.start || !dateRange.end) {
            return [];
        }

        // Calculate the number of days in the selected range
        const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
        
        // Generate dates for the selected range
        const rangeDates = Array.from({ length: dayCount }, (_, i) => 
            addDays(dateRange.start, i)
        );
        
        const sentimentPerDay = rangeDates.reduce((acc, date) => {
            acc[format(date, 'MM/dd')] = {
                positive: 0,
                negative: 0,
                neutral: 0
            };
            return acc;
        }, {});

        // Count sentiments for each day in the selected date range
        data.forEach(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp); // Use the reusable function
            if (callDate) {
                const rangeStart = startOfDay(dateRange.start);
                const rangeEnd = endOfDay(dateRange.end);
                
                if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    // Check for sentiment trend in different locations with proper fallback
                    let trend = null;
                    if (call.analytics?.sentiment_analysis?.trend) {
                        trend = call.analytics.sentiment_analysis.trend;
                    } else if (call.analytics?.user_sentiment) {
                        trend = call.analytics.user_sentiment;
                    }
                    
                    if (trend) {
                        const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' });
                        
                        if (sentimentPerDay.hasOwnProperty(formattedDate)) {
                            sentimentPerDay[formattedDate][trend]++;
                        }
                    }
                }
            }
        });

        // Convert to array format for chart
        const chartData = rangeDates.map(date => ({
            date: format(date, 'MM/dd'),
            ...sentimentPerDay[format(date, 'MM/dd')]
        }));

        return chartData;
    };

    const chartData = processCallData();
    const total = chartData.reduce(
        (sum, day) => sum + day.improving + day.declining + day.neutral,
        0
    );

    return (
        <div className="flex flex-col items-start card-container w-full h-[300px] space-y-2">
            <div className="text-sm text-font-primary mb-2">Sentiment Analysis</div>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -5 }}>
                    <XAxis dataKey="date" stroke="#b2b2b2" />
                    <YAxis stroke="#b2b2b2" />
                    <Tooltip />
                    <Bar dataKey="negative" stackId="sentiment" fill="#DB3A00" />
                    <Bar dataKey="neutral" stackId="sentiment" fill="#A3A3A370" />
                    <Bar dataKey="positive" stackId="sentiment" fill="#22c55e" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default SentimentChart;
  
